import { useCallback, useRef, useState } from 'react';

// Statements go to the analysis endpoint, which talks to YCharts server-side.
// The browser never calls YCharts directly: the app's CSP allows same-origin
// connections only, and the vendor token must not reach a client.
//
// What came back is shown to the planner by ExtractionReview.jsx before any of
// it is presented as analysis, because extraction from a PDF is the step most
// likely to be wrong and the planner is the one who can tell.

const ENDPOINT = '/api/AssessmentAnalysisService';
const MAX_BYTES = 10 * 1024 * 1024;

const readAsBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.slice(result.indexOf(',') + 1));
  };
  reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
  reader.readAsDataURL(file);
});

export default function StatementUpload({ analysis, onStatus, onBundle, clientName }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const status = analysis?.status || 'idle';
  const busy = status === 'uploading' || status === 'analyzing';

  const send = useCallback(async fileList => {
    // The button disables itself while a run is in flight, but a drop does not
    // go through the button. Two runs racing means the slower reply wins, so
    // the holdings on screen would be whichever analysis happened to finish
    // last rather than the one the advisor asked for.
    if (busy) return;

    const files = Array.from(fileList || []).filter(f => f && f.size);
    if (!files.length) return;

    const total = files.reduce((s, f) => s + f.size, 0);
    if (total > MAX_BYTES) {
      // Not "send them in smaller batches": an upload replaces the holdings
      // rather than adding to them, so a second batch would discard the first.
      onStatus({
        status: 'error',
        message: 'Those files come to more than 10 MB together, and they have to go up in one batch, '
          + 'because a new upload replaces the last one rather than adding to it. '
          + 'Try again without the largest statement.',
      });
      return;
    }

    onStatus({ status: 'uploading', message: `Reading ${files.length} statement${files.length > 1 ? 's' : ''}...` });
    try {
      const payload = {
        files: await Promise.all(files.map(async f => ({
          filename: f.name,
          contentBase64: await readAsBase64(f),
        }))),
        client: { name: clientName || '', asOf: new Date().toISOString().slice(0, 10) },
      };

      onStatus({ status: 'analyzing', message: 'Extracting holdings and running the analysis. This takes about a minute.' });
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json())?.error || ''; } catch { /* body is not json */ }
        throw new Error(detail || `The analysis service returned ${res.status}.`);
      }

      const bundle = await res.json();
      if (!bundle || !Array.isArray(bundle.holdings) || !bundle.holdings.length) {
        throw new Error('No holdings could be read from those statements.');
      }
      onBundle(bundle);
      onStatus({ status: 'done', message: `Analyzed ${bundle.holdings.length} positions.` });
    } catch (err) {
      // The report stays usable on failure: everything except the holdings
      // sections comes from the questionnaire and is unaffected.
      onStatus({ status: 'error', message: err.message || 'The analysis did not complete.' });
    }
  }, [busy, clientName, onBundle, onStatus]);

  return (
    <div className="no-print">
      <div
        className={`asmt-drop${dragOver ? ' is-over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(!busy); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); send(e.dataTransfer.files); }}
      >
        <button
          type="button"
          className="asmt-btn"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? 'Working...' : 'Choose statements'}
        </button>
        {/* This is the one sentence on the page an advisor would repeat to a
            client word for word, so it may only claim what can be shown.
            It read "analyzed and discarded, never stored", which was wrong
            about the vendor and wrong about the holdings, and the first attempt
            at fixing it replaced one unsupported claim with another by saying
            no copy is kept. That is a statement about what the provider does,
            and nothing here establishes it. What IS established: our side keeps
            nothing (the function touches no disk and no blob storage, and is
            stateless by design), the file is sent out to be read, and the
            provider exposes no delete, which is why the portfolio naming
            convention in ycharts.js is described there as the entire cleanup
            story. So the sentence claims our side only, and says plainly that
            the provider's retention is outside our reach. The retention
            question itself belongs to the vendor review, not to this copy.

            The provider is not named in the visible sentence on purpose. Which
            vendor reads the statement belongs in the engineering notes and the
            data-provenance record, not on a screen a prospect reads. */}
        <div className="asmt-drop-hint">
          or drop PDF statements here. We keep no copy of the file. It goes to our
          analysis provider to be read, and what they keep is not ours to delete. The
          holdings it produces stay with this report.
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          style={{ display: 'none' }}
          onChange={e => { send(e.target.files); e.target.value = ''; }}
        />
      </div>

      {status !== 'idle' && analysis?.message && (
        <div className={`asmt-status${status === 'error' ? ' is-error' : ''}`}>
          {busy && <span className="asmt-spinner" />}
          <span>{analysis.message}</span>
        </div>
      )}
    </div>
  );
}
